import { useEffect, useMemo, useState } from "react";
import { T } from "@/components/T";
import { useLang } from "@/context/LanguageContext";
import { fetchQuiz } from "@/lib/api";
import RichText from "@/components/RichText";
import { MandalaMotif } from "@/components/MotifBackground";
import { Check, X, RotateCcw, Award } from "lucide-react";
import { fireGamify } from "@/lib/gamify";

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

export default function Quiz() {
  const { pick } = useLang();
  const [all, setAll] = useState([]);
  const [round, setRound] = useState(0);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => { fetchQuiz().then(setAll).catch(() => {}); }, []);
  const questions = useMemo(
    () =>
      shuffle(all)
        .slice(0, 10)
        .map((qq) => {
          const opts = shuffle(qq.options.map((text, i) => ({ text, correct: i === qq.answer })));
          return { ...qq, options: opts.map((o) => o.text), answer: opts.findIndex((o) => o.correct) };
        }),
    [all, round]
  );
  const q = questions[idx];

  const choose = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === q.answer) { setScore((s) => s + 1); fireGamify("quiz", q.qid); }
  };
  const next = () => {
    if (idx + 1 >= questions.length) { setDone(true); return; }
    setIdx((n) => n + 1); setPicked(null);
  };
  const restart = () => { setRound((r) => r + 1); setIdx(0); setPicked(null); setScore(0); setDone(false); };

  return (
    <div data-testid="quiz-page">
      <section className="relative overflow-hidden border-b border-line">
        <MandalaMotif className="pointer-events-none absolute -left-16 -top-10 h-[420px] w-[420px] text-[var(--gold)] opacity-[0.06]" />
        <div className="mx-auto max-w-3xl px-5 py-14 text-center md:px-8 md:py-20">
          <p className="overline-label text-gold">{pick("धर्म प्रश्नोत्तरी", "Dharma Quiz")}</p>
          <h1 className="mt-3 font-deva text-4xl leading-tight text-ink deva-lh md:text-5xl">धर्म ज्ञान परीक्षा</h1>
          <p className="mt-5 font-serif-latin text-lg text-ink-2"><T>Test your knowledge of mantras, suktas, stotras, festivals and the temples of Bharat — ten questions each round.</T></p>
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-5 py-14 md:py-20">
        {!q && !done && <p className="text-center text-ink-3"><T>Loading questions…</T></p>}

        {q && !done && (
          <div data-testid="quiz-question" key={idx}>
            <div className="mb-6 flex items-center justify-between text-sm text-ink-3">
              <span>{pick("प्रश्न", "Question")} {idx + 1} / {questions.length}</span>
              <span className="overline-label text-gold">{q.category}</span>
            </div>
            <h2 className="font-serif-latin text-2xl leading-snug text-ink">{q.question}</h2>
            <div className="mt-8 space-y-3">
              {q.options.map((opt, i) => {
                const isAnswer = i === q.answer;
                const isPicked = picked === i;
                let cls = "border-line hover:border-[var(--ink)] text-ink-2";
                if (picked !== null && isAnswer) cls = "border-green-700/60 bg-green-700/5 text-ink";
                else if (isPicked && !isAnswer) cls = "border-red-700/50 bg-red-700/5 text-ink";
                return (
                  <button
                    key={i}
                    data-testid={`quiz-option-${i}`}
                    disabled={picked !== null}
                    onClick={() => choose(i)}
                    className={`flex w-full items-center justify-between gap-3 border px-5 py-4 text-left transition-colors ${cls}`}
                  >
                    <span className="font-serif-latin text-[17px]">{opt}</span>
                    {picked !== null && isAnswer && <Check className="h-5 w-5 shrink-0 text-green-700" />}
                    {isPicked && !isAnswer && <X className="h-5 w-5 shrink-0 text-red-700" />}
                  </button>
                );
              })}
            </div>
            {picked !== null && (
              <div className="mt-6 fade-up">
                <RichText value={q.explanation} className="border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-5 py-4 text-[15px] leading-relaxed text-ink-2" />
                <button data-testid="quiz-next-btn" onClick={next} className="mt-6 inline-flex items-center gap-2 bg-[var(--saffron)] px-10 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                  {idx + 1 >= questions.length ? pick("परिणाम देखें", "See result") : pick("अगला प्रश्न", "Next question")}
                </button>
              </div>
            )}
          </div>
        )}

        {done && (
          <div className="text-center fade-up" data-testid="quiz-result">
            <Award className="mx-auto h-14 w-14 text-gold" />
            <p className="mt-6 overline-label text-ink-3">{pick("आपका अंक", "Your score")}</p>
            <p className="mt-2 font-deva text-6xl text-ink">{score}<span className="text-3xl text-ink-3"> / {questions.length}</span></p>
            <p className="mt-4 font-serif-latin text-xl text-ink-2">
              {score >= 8 ? pick("अति उत्तम! आप धर्म-ज्ञानी हैं।", "Excellent — a true student of dharma!")
                : score >= 5 ? pick("बहुत अच्छा! अभ्यास जारी रखें।", "Well done — keep exploring!")
                : pick("एक अच्छी शुरुआत — पुनः प्रयास करें।", "A good beginning — try again!")}
            </p>
            <button data-testid="quiz-restart-btn" onClick={restart} className="mt-8 inline-flex items-center gap-2 border border-line px-6 py-3 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
              <RotateCcw className="h-4 w-4" /> {pick("पुनः खेलें", "Play again")}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
